import {
  ContextMenuItem,
  ContextMenuSurface,
} from '@src/components/common/contextMenuStyles';
import DeleteIcon from '@src/svgs/DeleteIcon';
import LineBreakIcon from '@src/svgs/LineBreakIcon';
import ReplaceIcon from '@src/svgs/ReplaceIcon';
import SimplePencilIcon from '@src/svgs/SimplePencilIcon';
import React, { useRef } from 'react';
import styled from 'styled-components';

import { useOutsideAlerter } from './useClickOutside';

export interface TranslatedPhraseContextMenuProps {
  positionXY: { x: number; y: number };
  /**
   * Open the phrase editor ("Fix Translation"). Optional since PLAT-11914: the
   * captions view opens this menu for a line break alone when the
   * Fix-Translation gesture is off.
   */
  onEdit?: () => void;
  onClose: () => void;
  /**
   * PLAT-11914 — add or remove a soft line break before the right-clicked word.
   * Wired ONLY by the captions view; `hasBreak` picks the label.
   */
  lineBreak?: { hasBreak: boolean; onToggle: () => void };
  /**
   * Remove the phrase's translation (PLAT-11340 follow-up). Wired ONLY by the
   * review workspace; the other three hosts (inline prose, redline diff,
   * captions view) pass nothing and render exactly the menu they did before.
   * Mutually exclusive with `onRestore` — the host passes whichever matches
   * the phrase's current state.
   */
  onRemove?: () => void;
  /** Restore an already-removed translation. See `onRemove`. */
  onRestore?: () => void;
}

/**
 * Right-click "Fix Translation" affordance on a translated phrase.
 *
 * PLAT-11515 — this used to be a green primary-filled pill with no icon. It was
 * a leftover: when the drag-select affordance (`SpanReplaceTrigger`) became a
 * two-action menu it moved onto the shared `contextMenuStyles` surface and this
 * one was never migrated, so the SAME action looked like a call-to-action from
 * one entry point and a menu row from the other. A filled green control is
 * reserved for a primary action, and a right-click affordance is a menu.
 *
 * It now renders the same surface, row, icon and label `SpanReplaceTrigger`
 * does, so the two are indistinguishable. Opens the phrase editor on click;
 * placed at the cursor; closes on click-outside.
 *
 * Remove / Restore (PLAT-11340 follow-up) are optional second rows, gated on
 * the host wiring a handler — the same containment idiom `SpanReplaceTrigger`
 * uses for `onReDub`, so a menu shared by four views doesn't grow an action
 * three of them never asked for.
 */
export const TranslatedPhraseContextMenu: React.FC<
  TranslatedPhraseContextMenuProps
> = ({ positionXY, onEdit, onClose, onRemove, onRestore, lineBreak }) => {
  const ref = useRef<HTMLDivElement>(null);
  useOutsideAlerter(ref, onClose);

  return (
    <Surface
      ref={ref}
      placement={{ left: positionXY.x, top: Math.max(10, positionXY.y - 10) }}
      // Suppress mousedown so the right-click selection isn't collapsed before
      // onClick runs (mirrors SpanReplaceTrigger).
      onMouseDown={e => e.preventDefault()}
      onMouseUp={e => e.stopPropagation()}
      role="menu"
    >
      {onEdit && (
        <ContextMenuItem
          type="button"
          role="menuitem"
          onClick={e => {
            e.stopPropagation();
            onEdit();
          }}
          aria-label="Fix translation for this phrase"
          // The testid names the ACTIONABLE affordance (what callers click), so
          // it stays on the row now that the surface wraps it.
          data-testid="translated-phrase-context-menu"
        >
          <SimplePencilIcon />
          Fix Translation
        </ContextMenuItem>
      )}
      {lineBreak && (
        <ContextMenuItem
          type="button"
          role="menuitem"
          onClick={e => {
            e.stopPropagation();
            lineBreak.onToggle();
          }}
          aria-label={
            lineBreak.hasBreak
              ? 'Remove the line break before this word'
              : 'Start a new line at this word'
          }
          data-testid="caption-line-break-toggle"
        >
          <LineBreakIcon />
          {lineBreak.hasBreak ? 'Remove Line Break' : 'Add Line Break'}
        </ContextMenuItem>
      )}
      {onRemove && (
        <ContextMenuItem
          type="button"
          role="menuitem"
          $danger
          onClick={e => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label="Remove the translation for this phrase"
          data-testid="translated-phrase-remove"
        >
          <DeleteIcon strokeColor="currentColor" />
          Remove Translation
        </ContextMenuItem>
      )}
      {onRestore && (
        <ContextMenuItem
          type="button"
          role="menuitem"
          onClick={e => {
            e.stopPropagation();
            onRestore();
          }}
          aria-label="Restore the removed translation for this phrase"
          data-testid="translated-phrase-restore"
        >
          <ReplaceIcon width={'16'} height={'14'} strokeColor="currentColor" />
          Restore Translation
        </ContextMenuItem>
      )}
    </Surface>
  );
};

const Surface = styled(ContextMenuSurface).attrs(
  (props: { placement: { left: number; top: number } }) => props,
)`
  left: ${p => p.placement.left}px;
  top: ${p => p.placement.top}px;
`;
